import { cn } from "@/lib/utils";

import { PreviewCanvasScale } from "./shared/preview-canvas-scale";

type CellShade =
  | "pulse-50"
  | "pulse-100"
  | "pulse-200"
  | "pulse-300"
  | "pulse-400"
  | "white"
  | "orange-50"
  | "orange-200"
  | "orange-300"
  | "orange-400";

interface HeatmapCell {
  value: string;
  shade: CellShade;
}

interface HeatmapColumn {
  month: string;
  accentHeader: boolean;
  cells: HeatmapCell[];
}

const SHADE_CLASS: Record<CellShade, string> = {
  "pulse-50": "bg-pulse-50",
  "pulse-100": "bg-pulse-100",
  "pulse-200": "bg-pulse-200",
  "pulse-300": "bg-pulse-300",
  "pulse-400": "bg-pulse-400",
  white: "bg-white",
  "orange-50": "bg-orange-50",
  "orange-200": "bg-orange-200",
  "orange-300": "bg-orange-300",
  "orange-400": "bg-orange-400",
};

/** Heatmap columns from Figma 11138:41851 (preview frame 11115:59300). */
const HEATMAP_COLUMNS: HeatmapColumn[] = [
  {
    month: "Jan",
    accentHeader: false,
    cells: [
      { shade: "pulse-400", value: "0.67" },
      { shade: "pulse-50", value: "0.92" },
      { shade: "pulse-50", value: "0.94" },
      { shade: "pulse-200", value: "0.77" },
      { shade: "pulse-100", value: "0.87" },
      { shade: "pulse-100", value: "0.84" },
      { shade: "pulse-200", value: "0.77" },
      { shade: "pulse-100", value: "0.90" },
    ],
  },
  {
    month: "Feb",
    accentHeader: false,
    cells: [
      { shade: "pulse-300", value: "0.70" },
      { shade: "pulse-50", value: "0.86" },
      { shade: "pulse-100", value: "0.81" },
      { shade: "pulse-100", value: "0.84" },
      { shade: "pulse-200", value: "0.79" },
      { shade: "pulse-100", value: "0.83" },
      { shade: "pulse-300", value: "0.73" },
      { shade: "pulse-100", value: "0.86" },
    ],
  },
  {
    month: "Mar",
    accentHeader: false,
    cells: [
      { shade: "pulse-200", value: "0.83" },
      { shade: "pulse-50", value: "0.92" },
      { shade: "pulse-50", value: "0.93" },
      { shade: "pulse-100", value: "0.90" },
      { shade: "pulse-100", value: "0.89" },
      { shade: "pulse-50", value: "0.95" },
      { shade: "pulse-100", value: "0.85" },
      { shade: "pulse-50", value: "0.96" },
    ],
  },
  {
    month: "Apr",
    accentHeader: false,
    cells: [
      { shade: "pulse-100", value: "0.89" },
      { shade: "pulse-50", value: "0.92" },
      { shade: "pulse-50", value: "0.92" },
      { shade: "pulse-100", value: "0.87" },
      { shade: "pulse-100", value: "0.86" },
      { shade: "pulse-100", value: "0.90" },
      { shade: "pulse-100", value: "0.87" },
      { shade: "pulse-50", value: "0.97" },
    ],
  },
  {
    month: "May",
    accentHeader: false,
    cells: [
      { shade: "pulse-50", value: "0.96" },
      { shade: "pulse-50", value: "0.93" },
      { shade: "pulse-50", value: "1.01" },
      { shade: "pulse-100", value: "0.89" },
      { shade: "pulse-50", value: "0.93" },
      { shade: "pulse-50", value: "0.98" },
      { shade: "pulse-100", value: "0.87" },
      { shade: "pulse-50", value: "0.97" },
    ],
  },
  {
    month: "Jun",
    accentHeader: false,
    cells: [
      { shade: "pulse-100", value: "0.92" },
      { shade: "pulse-50", value: "0.89" },
      { shade: "pulse-50", value: "1.02" },
      { shade: "pulse-100", value: "0.87" },
      { shade: "pulse-100", value: "0.91" },
      { shade: "pulse-50", value: "0.94" },
      { shade: "pulse-100", value: "0.89" },
      { shade: "pulse-50", value: "0.94" },
    ],
  },
  {
    month: "Jul",
    accentHeader: false,
    cells: [
      { shade: "pulse-50", value: "0.95" },
      { shade: "pulse-50", value: "0.91" },
      { shade: "pulse-50", value: "1.00" },
      { shade: "pulse-200", value: "0.81" },
      { shade: "pulse-50", value: "0.93" },
      { shade: "white", value: "1.02" },
      { shade: "pulse-50", value: "0.97" },
      { shade: "pulse-50", value: "0.99" },
    ],
  },
  {
    month: "Aug",
    accentHeader: false,
    cells: [
      { shade: "pulse-100", value: "0.89" },
      { shade: "pulse-50", value: "0.93" },
      { shade: "pulse-50", value: "0.98" },
      { shade: "pulse-200", value: "0.83" },
      { shade: "pulse-50", value: "0.98" },
      { shade: "orange-50", value: "1.04" },
      { shade: "pulse-50", value: "0.89" },
      { shade: "pulse-50", value: "1.00" },
    ],
  },
  {
    month: "Sep",
    accentHeader: true,
    cells: [
      { shade: "pulse-100", value: "0.89" },
      { shade: "pulse-50", value: "0.94" },
      { shade: "pulse-100", value: "0.88" },
      { shade: "pulse-100", value: "0.90" },
      { shade: "pulse-50", value: "0.96" },
      { shade: "orange-50", value: "1.02" },
      { shade: "pulse-50", value: "0.97" },
      { shade: "pulse-50", value: "0.99" },
    ],
  },
  {
    month: "Oct",
    accentHeader: true,
    cells: [
      { shade: "pulse-50", value: "0.98" },
      { shade: "orange-50", value: "1.04" },
      { shade: "pulse-100", value: "0.90" },
      { shade: "white", value: "0.96" },
      { shade: "orange-50", value: "1.03" },
      { shade: "orange-50", value: "1.05" },
      { shade: "orange-50", value: "1.03" },
      { shade: "orange-50", value: "1.02" },
    ],
  },
  {
    month: "Nov",
    accentHeader: true,
    cells: [
      { shade: "orange-300", value: "1.30" },
      { shade: "orange-200", value: "1.25" },
      { shade: "white", value: "1.02" },
      { shade: "orange-300", value: "1.34" },
      { shade: "orange-200", value: "1.23" },
      { shade: "orange-200", value: "1.19" },
      { shade: "orange-200", value: "1.23" },
      { shade: "orange-200", value: "1.19" },
    ],
  },
  {
    month: "Dec",
    accentHeader: true,
    cells: [
      { shade: "orange-400", value: "1.47" },
      { shade: "orange-50", value: "1.18" },
      { shade: "white", value: "0.99" },
      { shade: "orange-50", value: "1.12" },
      { shade: "orange-50", value: "1.11" },
      { shade: "orange-50", value: "1.10" },
      { shade: "orange-300", value: "1.35" },
      { shade: "pulse-50", value: "0.97" },
    ],
  },
];

function HeatmapHeader({
  month,
  accent,
  isFirstCol,
  isLastCol,
}: {
  month: string;
  accent: boolean;
  isFirstCol: boolean;
  isLastCol: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-[20.634px] items-start px-[5.895px] py-[4.422px]",
        accent
          ? "bg-pulse-700 text-white"
          : "border-grey-100 border-t-[0.44px] border-r-[0.44px]",
        isFirstCol && "rounded-tl-[4px] border-l-[0.44px]",
        isLastCol && "rounded-tr-[5.895px]"
      )}
    >
      <span
        className={cn(
          "text-[8.843px] leading-[1.3] font-semibold tracking-[-0.1769px] whitespace-nowrap",
          accent ? "text-white" : "text-grey-500"
        )}
        style={{ fontFeatureSettings: '"zero" 1, "ss02" 1' }}
      >
        {month}
      </span>
    </div>
  );
}

function HeatmapCellView({
  cell,
  isFirstCol,
  isLastCol,
  isLastRow,
}: {
  cell: HeatmapCell;
  isFirstCol: boolean;
  isLastCol: boolean;
  isLastRow: boolean;
}) {
  return (
    <div
      className={cn(
        "border-grey-100 flex h-[20.634px] items-start border-t-[0.442px] border-r-[0.442px] px-[5.895px] py-[4.422px]",
        SHADE_CLASS[cell.shade],
        isFirstCol && "border-l-[0.442px]",
        isLastRow && "border-b-[0.442px]",
        isFirstCol && isLastRow && "rounded-bl-[5.895px]",
        isLastCol && isLastRow && "rounded-br-[5.895px]"
      )}
    >
      <span
        className="text-grey-950 w-full text-right text-[8.843px] leading-[1.2] tracking-[-0.1769px]"
        style={{ fontFeatureSettings: '"zero" 1, "ss02" 1' }}
      >
        {cell.value}
      </span>
    </div>
  );
}

/**
 * Clearance ladder card inset (Figma 11115:59300, 391×291).
 * Scales down on the mobile preview shell; 1:1 from md+.
 */
export function ClearanceLadderPreview() {
  return (
    <PreviewCanvasScale testId="clearance-ladder-canvas">
      <p className="text-grey-950 absolute top-[23px] left-[24px] text-[12px] leading-[1.4] font-normal whitespace-nowrap">
        Stock Schedule
      </p>

      {/* Table — Figma 11138:41851 at left 24, top 58 (371.406×185.703) */}
      <div className="absolute top-[58px] left-[24px] flex h-[185.703px] w-[371.406px]">
        {HEATMAP_COLUMNS.map((column, colIndex) => {
          const isFirstCol = colIndex === 0;
          const isLastCol = colIndex === HEATMAP_COLUMNS.length - 1;

          return (
            <div key={column.month} className="w-[30.951px] shrink-0">
              <HeatmapHeader
                month={column.month}
                accent={column.accentHeader}
                isFirstCol={isFirstCol}
                isLastCol={isLastCol}
              />
              {column.cells.map((cell, rowIndex) => (
                <HeatmapCellView
                  key={`${column.month}-${rowIndex}`}
                  cell={cell}
                  isFirstCol={isFirstCol}
                  isLastCol={isLastCol}
                  isLastRow={rowIndex === column.cells.length - 1}
                />
              ))}
            </div>
          );
        })}
      </div>
    </PreviewCanvasScale>
  );
}
